import React, { useState } from "react";

import { useAuth } from "oidc-react";

import {
  AccountCircle,
  Payments,
  Cloud,
  Settings,
  ContentCopy,
  OpenInNew,
  Logout,
} from "@mui/icons-material";
import {
  Button,
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  Grid,
  Chip,
  Link as MuiLink,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import Gravatar from "../components/gravatar";
import Loading from "../components/loader";
import { stripeApiConfig } from "../config";
import useProfile from "../hooks/profile";

export function Profile() {
  const auth = useAuth();
  const { profile: p, loading } = useProfile();
  const [showAwsDetails, setShowAwsDetails] = useState(false);

  const logout = () => {
    auth.signOutRedirect();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return <Loading text="Loading profile" />;
  }

  const generateAwsConsoleUrl = (accountId: string) => {
    if (!accountId) return "";
    return `https://console.aws.amazon.com/?accountId=${accountId}`;
  };

  const renderAwsDialog = () => (
    <Dialog
      open={showAwsDetails}
      onClose={() => setShowAwsDetails(false)}
      maxWidth="md"
    >
      <DialogTitle>AWS Account Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Account ID
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body1">
              {p.aws.id || "Not available"}
            </Typography>
            {p.aws.id && (
              <IconButton
                size="small"
                component={MuiLink}
                href={generateAwsConsoleUrl(p.aws.id)}
                target="_blank"
                rel="noopener"
                sx={{ ml: 1 }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            MicroARN
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "background.paper",
              p: 1,
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", overflowX: "auto", flexGrow: 1 }}
            >
              {p.aws.microARN || "Not available"}
            </Typography>
            {p.aws.microARN && (
              <IconButton
                size="small"
                onClick={() => copyToClipboard(p.aws.microARN)}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Role
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "background.paper",
              p: 1,
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", overflowX: "auto", flexGrow: 1 }}
            >
              {p.aws.role || "Not available"}
            </Typography>
            {p.aws.role && (
              <IconButton
                size="small"
                onClick={() => copyToClipboard(p.aws.role)}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowAwsDetails(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            mb: 4,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            boxShadow: "0 1px 0 rgba(16, 26, 47, 0.04), 0 1px 2px rgba(16, 26, 47, 0.06)",
          }}
        >
          <Box
            sx={{
              mb: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Gravatar
                email={auth?.userData?.profile?.email || ""}
                size={80}
                sx={{
                  mr: 3,
                  border: "2px solid",
                  borderColor: "divider",
                  boxShadow: "0 2px 4px rgba(16, 26, 47, 0.06)",
                }}
              />
              <Box>
                <Typography variant="h4" gutterBottom>
                  {auth?.userData?.profile?.name || "User Profile"}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {auth?.userData?.profile?.email}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Logout />}
              onClick={logout}
              sx={{ borderRadius: 2 }}
            >
              Log out
            </Button>
          </Box>

          <Divider sx={{ mb: 4 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Payments sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6">Billing Information</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body1">Stripe Account</Typography>
                    <Chip
                      label={p.stripe ? "Connected" : "Not Connected"}
                      color={p.stripe ? "success" : "error"}
                      size="small"
                    />
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<OpenInNew />}
                    component={MuiLink}
                    href={`https://billing.stripe.com/login/${stripeApiConfig.login}`}
                    target="_blank"
                    rel="noopener"
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      textDecoration: "none",
                    }}
                  >
                    Manage Billing
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Cloud sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6">AWS Account</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body1">AWS Account ID</Typography>
                    {p.aws.id ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {p.aws.id}
                        </Typography>
                        <IconButton
                          size="small"
                          component={MuiLink}
                          href={generateAwsConsoleUrl(p.aws.id)}
                          target="_blank"
                          rel="noopener"
                          sx={{ ml: 0.5 }}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Chip label="Not set up" color="error" size="small" />
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body1">Permissions</Typography>
                    <Chip
                      label={p.aws.canAssume ? "Verified" : "Not Configured"}
                      color={p.aws.canAssume ? "success" : "error"}
                      size="small"
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowAwsDetails(true)}
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                    startIcon={<Settings />}
                    disabled={!p.aws.id}
                  >
                    View AWS Details
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

        </Paper>
      </Container>

      {renderAwsDialog()}
    </>
  );
}
